import { useCallback, useRef, useState } from "react";
import { AgentWorkflowDefinition } from "@/store/agentsStore";
import equal from "fast-deep-equal";
const DEFAULT_HISTORY_LIMIT = 50;

const areDefinitionsEqual = (
  a: AgentWorkflowDefinition,
  b: AgentWorkflowDefinition,
) => equal(a, b);

const cloneDefinition = (
  definition: AgentWorkflowDefinition,
): AgentWorkflowDefinition => {
  if (typeof structuredClone === "function") {
    return structuredClone(definition);
  }
  return JSON.parse(JSON.stringify(definition));
};

interface UseWorkflowHistoryArgs {
  limit?: number;
}

export interface WorkflowHistoryApi {
  canRedo: boolean;
  canUndo: boolean;
  recordSnapshot: (definition: AgentWorkflowDefinition) => void;
  redo: () => AgentWorkflowDefinition | null;
  resetHistory: (definition: AgentWorkflowDefinition) => void;
  undo: () => AgentWorkflowDefinition | null;
}

export const useWorkflowHistory = (
  args: UseWorkflowHistoryArgs = {},
): WorkflowHistoryApi => {
  const limit = args.limit ?? DEFAULT_HISTORY_LIMIT;
  const historyRef = useRef<AgentWorkflowDefinition[]>([]);
  const indexRef = useRef(-1);
  const [index, setIndex] = useState(-1);

  const updateIndex = useCallback((nextIndex: number) => {
    indexRef.current = nextIndex;
    setIndex(nextIndex);
  }, []);

  const recordSnapshot = useCallback(
    (definition: AgentWorkflowDefinition) => {
      const snapshot = cloneDefinition(definition);
      const history = historyRef.current;
      const currentEntry =
        indexRef.current >= 0 ? history[indexRef.current] : null;
      if (currentEntry && areDefinitionsEqual(currentEntry, snapshot)) {
        return;
      }

      const truncatedHistory = history.slice(0, indexRef.current + 1);
      truncatedHistory.push(snapshot);

      let nextHistory = truncatedHistory;
      let nextIndex = truncatedHistory.length - 1;

      if (truncatedHistory.length > limit) {
        nextHistory = truncatedHistory.slice(truncatedHistory.length - limit);
        nextIndex = nextHistory.length - 1;
      }

      historyRef.current = nextHistory;
      updateIndex(nextIndex);
    },
    [limit, updateIndex],
  );

  const undo = useCallback(() => {
    if (indexRef.current <= 0) {
      return null;
    }
    const nextIndex = indexRef.current - 1;
    updateIndex(nextIndex);
    return cloneDefinition(historyRef.current[nextIndex]);
  }, [updateIndex]);

  const redo = useCallback(() => {
    if (
      indexRef.current < 0 ||
      indexRef.current >= historyRef.current.length - 1
    ) {
      return null;
    }
    const nextIndex = indexRef.current + 1;
    updateIndex(nextIndex);
    return cloneDefinition(historyRef.current[nextIndex]);
  }, [updateIndex]);

  const resetHistory = useCallback(
    (definition: AgentWorkflowDefinition) => {
      const baseSnapshot = cloneDefinition(definition);
      historyRef.current = [baseSnapshot];
      updateIndex(0);
    },
    [updateIndex],
  );

  const historyLength = historyRef.current.length;
  const canUndo = index > 0;
  const canRedo = index >= 0 && index < historyLength - 1 && historyLength > 0;

  return {
    canRedo,
    canUndo,
    recordSnapshot,
    redo,
    resetHistory,
    undo,
  };
};
